import { useEffect, useState } from "react";
import { UploadSvg } from "../../svgFiles/UploadSvg";
import { FileSvg } from "../../svgFiles/FileSvg";
import { AiSparkleSvg } from "../../svgFiles/AiSparkleSvg";
import PrescriptionList from "../../features/prescription/PrescriptionList";
import { useGetPrescriptionQuery } from "../../app/prescriptionApi/prescriptionApi";
import {
  PRESCRIPTION_FORM_INITIAL,
  PRESCRIPTION_UPLOAD_ACCEPT,
  getPrescriptionUploadError,
  prescriptionFormToVa,
  vaPrescriptionToFormValues,
  unwrapOptionValue,
} from "../../helpers/prescriptionFormat";
import { openPrescriptionFile } from "../../helpers/vaParseStorage";
import { showToast } from "../../helpers/Utils";
import PrescriptionExtractCompare from "./PrescriptionExtractCompare";
import Button from "../shared/buttons/button";
import Loader from "../shared/loader/Loader";
import "../../features/prescription/prescription.css";

const PRESCRIPTION_TYPE = "Progressive Lenses";

const EMPTY_PRESCRIPTION = {
  od: {},
  os: {},
  pdType: null,
  fileUrl: null,
  fileName: null,
  extractMeta: null,
  prescriptionFileKey: null,
  multiplePrescriptions: false,
  singularPd: null,
  formValues: null,
};

export default function PrescriptionSection({
  prescription,
  onChange,
  onPdTypeChange,
  onFileUpload,
  onAiExtract,
  requiresPrescription = false,
  errors = {},
  parsingFile = false,
  parseError = "",
}) {
  const rx = prescription ?? EMPTY_PRESCRIPTION;
  const [formValues, setFormValues] = useState(() =>
    vaPrescriptionToFormValues(rx)
  );
  const [selectedOption, setSelectedOption] = useState(rx.pdType || null);
  const hasUploadedFile = Boolean(rx.fileName);

  const { data: prescriptionData, isLoading } = useGetPrescriptionQuery({
    prescriptionType: PRESCRIPTION_TYPE,
  });

  // Re-hydrate when parent prescription changes (e.g. parse result load)
  useEffect(() => {
    setFormValues(vaPrescriptionToFormValues(rx));
    setSelectedOption(rx.pdType || null);
    // Only sync when file / pdType / nested values identity changes from parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rx.fileName,
    rx.pdType,
    rx.od?.sph,
    rx.od?.sphere,
    rx.od?.cyl,
    rx.od?.cylinder,
    rx.od?.axis,
    rx.od?.add,
    rx.od?.pd,
    rx.os?.sph,
    rx.os?.sphere,
    rx.os?.cyl,
    rx.os?.cylinder,
    rx.os?.axis,
    rx.os?.add,
    rx.os?.pd,
    rx.singularPd,
    rx.extractMeta,
  ]);

  const emitChange = (nextFormValues, nextPdType, fileMeta = {}) => {
    const next = prescriptionFormToVa(nextFormValues, nextPdType, null);
    onChange({
      ...next,
      fileName: fileMeta.fileName !== undefined ? fileMeta.fileName : rx.fileName,
      fileUrl: fileMeta.fileUrl !== undefined ? fileMeta.fileUrl : rx.fileUrl,
      extractMeta: rx.extractMeta,
      prescriptionFileKey: rx.prescriptionFileKey,
      multiplePrescriptions: rx.multiplePrescriptions,
      hasAdd: rx.hasAdd,
    });
  };

  const handleSelect = (field, value) => {
    const nextValue =
      field === "PDOneorTwoNumbers" ? value : unwrapOptionValue(value);
    const nextPdType =
      field === "PDOneorTwoNumbers" ? nextValue : selectedOption;
    if (field === "PDOneorTwoNumbers") {
      setSelectedOption(nextValue);
      onPdTypeChange?.(nextValue);
    }
    const nextFormValues = {
      ...PRESCRIPTION_FORM_INITIAL,
      ...formValues,
      prescriptionType: PRESCRIPTION_TYPE,
      [field]: nextValue,
      PDOneorTwoNumbers: nextPdType,
    };
    setFormValues(nextFormValues);
    emitChange(nextFormValues, nextPdType);
  };

  const handlePdOptionChange = (option) => {
    setSelectedOption(option);
    const nextFormValues = {
      ...PRESCRIPTION_FORM_INITIAL,
      ...formValues,
      prescriptionType: PRESCRIPTION_TYPE,
      PDOneorTwoNumbers: option,
    };
    setFormValues(nextFormValues);
    onPdTypeChange?.(option);
    emitChange(nextFormValues, option);
  };

  const handleDeleteFile = () => {
    if (typeof rx.fileUrl === "string" && rx.fileUrl.startsWith("blob:")) {
      URL.revokeObjectURL(rx.fileUrl);
    }
    const cleared = { ...EMPTY_PRESCRIPTION };
    setFormValues({
      ...PRESCRIPTION_FORM_INITIAL,
      prescriptionType: PRESCRIPTION_TYPE,
    });
    setSelectedOption(null);
    onChange(cleared);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const error = getPrescriptionUploadError(file);
    if (error) {
      showToast(error, "error");
      return;
    }
    onFileUpload?.(file);
  };

  return (
    <div className="va-prescription-section">
      <div className="va-prescription-section__header">
        <h6 className="va-prescription-section__title mb-0">Prescription</h6>
        <div className="va-prescription-section__upload d-flex align-items-center gap-3">
          {!hasUploadedFile ? (
            <label className="btn btn-link p-0 d-flex align-items-center gap-2 text-decoration-none mb-0">
              <UploadSvg />
              <input
                type="file"
                className="d-none"
                accept={PRESCRIPTION_UPLOAD_ACCEPT}
                onChange={handleFileInputChange}
              />
              upload prescription file
            </label>
          ) : (
            <>
              <button
                type="button"
                className="va-prescription-section__file-link"
                onClick={() => {
                  if (rx.fileUrl) openPrescriptionFile(rx.fileUrl);
                }}
                disabled={!rx.fileUrl}
              >
                <FileSvg />
                Prescription file
              </button>
              {typeof onAiExtract === "function" && (
                <Button
                  size="small"
                  label="AI Extraction"
                  className="va-prescription-section__ai-btn"
                  icon={<AiSparkleSvg color="#fff" />}
                  onClick={onAiExtract}
                  loading={parsingFile}
                  disabled={parsingFile}
                />
              )}
              <label className="btn btn-link p-0 small mb-0 text-decoration-none">
                <input
                  type="file"
                  className="d-none"
                  accept={PRESCRIPTION_UPLOAD_ACCEPT}
                  onChange={handleFileInputChange}
                />
                Re-upload
              </label>
              <button
                type="button"
                className="btn btn-link btn-sm p-0 text-danger"
                onClick={handleDeleteFile}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      {parsingFile && (
        <div className="text-center py-3">
          <Loader />
          <p className="mb-0 mt-2 small text-muted">Reading prescription…</p>
        </div>
      )}

      {parseError && <p className="text-danger small">{parseError}</p>}

      {rx.extractMeta && !parsingFile && (
        <PrescriptionExtractCompare
          fileUrl={rx.fileUrl}
          fileName={rx.fileName}
          extractMeta={rx.extractMeta}
          issues={rx.extractMeta?.issues}
        />
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <PrescriptionList
          listData={prescriptionData}
          selectedOption={selectedOption}
          setSelectedOption={handlePdOptionChange}
          handleSelect={handleSelect}
          formValues={formValues}
          currentOrder={{ prescriptionType: PRESCRIPTION_TYPE }}
          errors={errors}
        />
      )}

      {errors.prescription && (
        <p className="text-danger small mt-2 mb-0">{errors.prescription}</p>
      )}
    </div>
  );
}
